<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import { megamenuSalesPath } from '~/data/megamenu'
import { isExternalUrl } from '~/utils/urlHelpers'
import IconChevronLeft from '~/assets/icons/chevron-left.svg?component'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import IconLightning from '~/assets/icons/lightning-charge-fill.svg?component'
import { sendMenuClick } from '~/composables/useGtmEvents'

const props = defineProps<{
  item: MegamenuNavItem
  title: string
  /** When set, show only this section's content. When null, show item's primary link + top-level items (e.g. kits). */
  section?: MegamenuSection | null
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'navbarClose'): void
}>()

const { baseUrl } = useRuntimeConfig().public as { baseUrl: string }
const resolveHref = useFullLinkUrl()
const isExternal = (href: string) => isExternalUrl(href, baseUrl)

function isSalesSection(section: MegamenuSection) {
  return section.title === 'Sales'
}

/** Single section view: render that section's items/subSections/Sales */
const isSingleSection = computed(() => props.section != null)

function handleSalesClick() {
  if (!props.section) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: props.section.title,
    itemLabel: 'Sales',
    itemPath: megamenuSalesPath,
  })
  emit('navbarClose')
}

function handleSubSectionItemClick(
  sub: { title: string },
  link: { label: string; href?: string }
) {
  if (!props.section || !link.href) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: props.section.title,
    subSectionTitle: sub.title,
    itemLabel: link.label,
    itemPath: link.href,
  })
  emit('navbarClose')
}

function handleSectionFlatItemClick(link: { label: string; href?: string }) {
  if (!props.section || !link.href) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: props.section.title,
    itemLabel: link.label,
    itemPath: link.href,
  })
  emit('navbarClose')
}

function handlePrimaryLinkClick() {
  if (!props.item.primaryLink) return
  sendMenuClick({
    menuName: props.item.label,
    itemLabel: props.item.primaryLink.label,
    itemPath: props.item.primaryLink.href,
  })
  emit('navbarClose')
}

function handleTopLevelItemClick(link: { label: string; href?: string }) {
  if (!link.href) return
  sendMenuClick({
    menuName: props.item.label,
    itemLabel: link.label,
    itemPath: link.href,
  })
  emit('navbarClose')
}
</script>

<template>
  <div data-test-id="megamenu-mobile-panel" class="flex flex-col h-full min-h-0">
    <header
      data-test-id="megamenu-mobile-panel-header"
      class="flex items-center gap-2 shrink-0 border-b border-gray-200 pb-2 px-4"
    >
      <button
        data-test-id="megamenu-mobile-panel-back-button"
        type="button"
        class="p-2 -ml-2 text-stone-700 hover:bg-gray-100 rounded-full"
        aria-label="Back"
        @click="emit('back')"
      >
        <IconChevronLeft class="h-5 w-5" />
      </button>
      <h2 class="text-lg text-center w-full font-semibold text-stone-900">
        {{ title }}
      </h2>
    </header>
    <div data-test-id="megamenu-mobile-panel-content" class="flex-1 overflow-auto min-h-0">
      <!-- Single section: Sales -->
      <NuxtLinkLocale
        v-if="isSingleSection && section && isSalesSection(section)"
        data-test-id="megamenu-mobile-panel-sales-link"
        :to="resolveHref(megamenuSalesPath)"
        class="flex items-center gap-2 rounded-full bg-amber-100 px-4 py-3 my-2 text-amber-900 no-underline font-medium"
        @click="handleSalesClick"
      >
        <IconLightning class="h-5 w-5 shrink-0 text-red-500" />
        {{ section.title }}
      </NuxtLinkLocale>
      <!-- Single section: subSections (e.g. Top Kategorien) -->
      <div
        v-else-if="isSingleSection && section?.subSections?.length"
        data-test-id="megamenu-mobile-panel-subsections"
        class="flex flex-col"
      >
        <template v-for="sub in section.subSections" :key="sub.title">
          <div
            :data-test-id="`megamenu-mobile-panel-subsection-${sub.title}`"
            class="border-b border-gray-100"
          >
            <div class="py-2 pl-4 text-sm font-medium text-stone-500">
              {{ sub.title }}
            </div>
            <ul>
              <li
                v-for="(link, i) in sub.items?.filter((l) => l?.label)"
                :key="`${sub.title}-${i}`"
                :data-test-id="`megamenu-mobile-panel-subsection-${sub.title}-item-${i}`"
                class="border-t border-gray-50"
              >
                <a
                  v-if="link.href && isExternal(link.href)"
                  :href="resolveHref(link.href)"
                  class="flex items-center justify-between py-2.5 pl-4 text-stone-800 no-underline hover:text-teal-600"
                  target="_blank"
                  rel="noopener"
                  @click="handleSubSectionItemClick(sub, link)"
                >
                  {{ link.label }}
                </a>
                <NuxtLinkLocale
                  v-else-if="link.href"
                  :to="resolveHref(link.href)"
                  class="flex items-center justify-between py-2.5 pl-4 text-stone-800 no-underline hover:text-teal-600"
                  @click="handleSubSectionItemClick(sub, link)"
                >
                  {{ link.label }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </div>
        </template>
      </div>
      <!-- Single section: items -->
      <div
        v-else-if="isSingleSection && section?.items?.length"
        data-test-id="megamenu-mobile-panel-items"
        class="flex flex-col"
      >
        <template v-if="section.items.length === 1 && section.items[0]?.href">
          <a
            data-test-id="megamenu-mobile-panel-item-0"
            v-if="isExternal(section.items[0].href)"
            :href="resolveHref(section.items[0].href)"
            class="flex items-center justify-between py-3 text-stone-800 no-underline hover:text-teal-600"
            target="_blank"
            rel="noopener"
            @click="handleSectionFlatItemClick(section.items[0])"
          >
            {{ section.items[0].label }}
            <IconChevronRight class="h-4 w-4 text-gray-400" />
          </a>
          <NuxtLinkLocale
            v-else
            :to="resolveHref(section.items[0].href)"
            class="flex items-center justify-between py-3 text-stone-800 no-underline hover:text-teal-600"
            @click="handleSectionFlatItemClick(section.items[0])"
          >
            {{ section.items[0].label }}
            <IconChevronRight class="h-4 w-4 text-gray-400" />
          </NuxtLinkLocale>
        </template>
        <ul v-else>
          <li
            v-for="(link, i) in section.items.filter((l) => l?.label)"
            :key="i"
            :data-test-id="`megamenu-mobile-panel-item-${i}`"
            class="border-t border-gray-50 pl-4"
          >
            <a
              v-if="link.href && isExternal(link.href)"
              :href="resolveHref(link.href)"
              class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
              target="_blank"
              rel="noopener"
              @click="handleSectionFlatItemClick(link)"
            >
              {{ link.label }}
            </a>
            <NuxtLinkLocale
              v-else-if="link.href"
              :to="resolveHref(link.href)"
              class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
              @click="handleSectionFlatItemClick(link)"
            >
              {{ link.label }}
            </NuxtLinkLocale>
          </li>
        </ul>
      </div>
      <!-- No section: item with primaryLink + top-level items (kits) -->
      <template v-else>
        <div
          v-if="item.primaryLink"
          data-test-id="megamenu-mobile-panel-primary-link"
          class="border-b border-gray-100"
        >
          <a
            v-if="isExternal(item.primaryLink.href)"
            :href="resolveHref(item.primaryLink.href)"
            class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
            target="_blank"
            rel="noopener"
            @click="handlePrimaryLinkClick"
          >
            {{ item.primaryLink.label }}
          </a>
          <NuxtLinkLocale
            v-else
            :to="resolveHref(item.primaryLink.href)"
            class="flex items-center justify-between py-3 pl-4 text-stone-800 no-underline hover:text-teal-600"
            @click="handlePrimaryLinkClick"
          >
            {{ item.primaryLink.label }}
          </NuxtLinkLocale>
        </div>
        <ul v-if="item.items?.length" data-test-id="megamenu-mobile-panel-top-level-items">
          <li
            v-for="(link, i) in item.items"
            :key="i"
            :data-test-id="`megamenu-mobile-panel-top-level-item-${i}`"
            class="border-t border-gray-50 pl-4"
          >
            <a
              v-if="link.href && isExternal(link.href)"
              :href="resolveHref(link.href)"
              class="flex items-center justify-between py-2.5 pl-0 text-stone-800 no-underline hover:text-teal-600"
              target="_blank"
              rel="noopener"
              @click="handleTopLevelItemClick(link)"
            >
              {{ link.label }}
            </a>
            <NuxtLinkLocale
              v-else-if="link.href"
              :to="resolveHref(link.href)"
              class="flex items-center justify-between py-2.5 pl-0 text-stone-800 no-underline hover:text-teal-600"
              @click="handleTopLevelItemClick(link)"
            >
              {{ link.label }}
            </NuxtLinkLocale>
          </li>
        </ul>
      </template>
    </div>
  </div>
</template>
